import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import HomeworkDialog from "@/components/HomeworkDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Save, Plus, Trash2, Pencil, Sunrise, Sun, BookOpen, Video, ExternalLink, ChevronsUpDown, Check, CalendarDays, ChevronUp, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import JitsiRoomOverlay from "@/components/JitsiRoomOverlay";
import DailyRoomOverlay from "@/components/DailyRoomOverlay";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { to12h } from "@/lib/time";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";

// Consistent per-subject color chip (hash subject name -> palette index).
const SUBJECT_COLORS = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
];
function subjectColor(s) {
  if (!s) return "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300";
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return SUBJECT_COLORS[h % SUBJECT_COLORS.length];
}

// Searchable teacher picker — filters by the period's subject, supports typing.
function TeacherCombobox({ value, onChange, teachers = [], subject, testid }) {
  const [open, setOpen] = useState(false);
  const norm = (x) => (x || "").toLowerCase().trim();
  const subj = norm(subject);
  const subsOf = (t) => (t.subjects && t.subjects.length ? t.subjects : (t.subject ? [t.subject] : []));
  const matched = subj
    ? teachers.filter((t) =>
        subsOf(t).some((s) => {
          const ts = norm(s);
          return ts && (ts === subj || subj.includes(ts) || ts.includes(subj));
        })
      )
    : teachers;
  const list = matched.length ? matched : teachers;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="h-9 rounded-lg min-w-[120px] w-full border border-slate-200 dark:border-slate-700 bg-transparent px-3 flex items-center justify-between gap-1 text-sm text-slate-900 dark:text-slate-100"
        >
          <span className={value ? "truncate" : "text-slate-400 truncate"}>{value || "Select"}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[220px]" align="start">
        <Command>
          <CommandInput placeholder="Search teacher…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No teacher found.</CommandEmpty>
            <CommandGroup>
              {list.map((t) => (
                <CommandItem
                  key={t.id || t.name}
                  value={t.name}
                  onSelect={() => { onChange(t.name); setOpen(false); }}
                  data-testid={`${testid}-opt-${t.name}`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${value === t.name ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{t.name}</span>
                  {t.subject ? <span className="ml-auto text-[10px] text-slate-400 truncate">{t.subject}</span> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Infer AM/PM block from start_time when missing (< 13:00 = AM, else PM).
const inferBlock = (p) => {
  if (p.block === "AM" || p.block === "PM") return p.block;
  const st = (p.start_time || "").trim();
  const hh = parseInt(st.split(":")[0] || "0", 10);
  return Number.isFinite(hh) && hh < 13 ? "AM" : "PM";
};

export default function Timetable() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isReadOnly = user?.role === "super_admin";
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [cn, setCn] = useState(localStorage.getItem("sa_selected_class") || "");
  const [sec, setSec] = useState(localStorage.getItem("sa_selected_section") || "");
  const [classMeta, setClassMeta] = useState({ classes: [], assignments: [] });
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [defaultsMode, setDefaultsMode] = useState(false);
  const [weekday, setWeekday] = useState((new Date().getDay() + 6) % 7); // Mon=0 .. Sun=6
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [ocOpen, setOcOpen] = useState(false);
  const [homeworkFor, setHomeworkFor] = useState(null); // {period} when dialog open
  const [homeworkByPeriod, setHomeworkByPeriod] = useState({});
  const [options, setOptions] = useState({ subjects: [], teachers: [] });
  const [nowMin, setNowMin] = useState(() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); });

  // Tick every 30s so the "running now" highlight tracks the live period.
  useEffect(() => {
    const id = setInterval(() => { const d = new Date(); setNowMin(d.getHours() * 60 + d.getMinutes()); }, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!cn) return;
    api.get("/timetable/options", { params: { class_name: cn } })
      .then(({ data }) => setOptions(data)).catch(() => {});
  }, [cn]);

  const selectClass = (c, s) => {
    localStorage.setItem("sa_selected_class", c);
    localStorage.setItem("sa_selected_section", s);
    setCn(c); setSec(s);
  };

  useEffect(() => {
    api.get("/meta/classes").then(({ data }) => {
      setClassMeta(data);
      const pairs = data.assignments || [];
      const hasCurrent = cn && sec && pairs.some((a) => a.class_name === cn && a.section === sec);
      if (!hasCurrent && pairs.length) selectClass(pairs[0].class_name, pairs[0].section);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const classOptions = classMeta.classes?.length ? classMeta.classes : (cn ? [cn] : []);
  const sectionOptions = (() => {
    const secs = (classMeta.assignments || []).filter((a) => a.class_name === cn).map((a) => a.section);
    const uniq = [...new Set(secs)].sort();
    return uniq.length ? uniq : (sec ? [sec] : []);
  })();

  const load = async () => {
    if (!cn || !sec) return;
    setLoading(true);
    try {
      if (defaultsMode) {
        const { data } = await api.get("/timetable/default", { params: { class_name: cn, section: sec, weekday } });
        setPeriods((data.periods || []).map((p) => ({ ...p, block: inferBlock(p) })));
      } else {
        const { data } = await api.get("/timetable", { params: { class_name: cn, section: sec, date } });
        // Ensure every period has a block tag so the AM/PM split works without further user input
        setPeriods((data.periods || []).map((p) => ({ ...p, block: inferBlock(p) })));
      }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  const loadHomework = async () => {
    if (!cn || !sec) return;
    try {
      const { data } = await api.get("/homework", { params: { class_name: cn, section: sec, date } });
      const map = {};
      for (const h of data) {
        (map[h.period] = map[h.period] || []).push(h);
      }
      setHomeworkByPeriod(map);
    } catch (e) { console.warn("homework load failed:", e); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); if (!defaultsMode) loadHomework(); }, [cn, sec, date, defaultsMode, weekday]);

  // Find the absolute index of the i-th period in a given block
  const findGlobalIndex = (block, localIdx) => {
    let count = -1;
    for (let g = 0; g < periods.length; g++) {
      if (periods[g].block === block) {
        count++;
        if (count === localIdx) return g;
      }
    }
    return -1;
  };

  const updateAt = (g, key, val) => {
    setPeriods((p) => p.map((row, idx) => (idx === g ? { ...row, [key]: val } : row)));
  };

  const addPeriodToBlock = (block) => {
    setPeriods((p) => {
      const lastInBlock = [...p].reverse().find((x) => x.block === block);
      const insertIdx = block === "AM"
        ? (p.findIndex((x) => x.block === "PM") === -1 ? p.length : p.findIndex((x) => x.block === "PM"))
        : p.length;
      const newRow = {
        period: 0, // re-numbered below
        subject: "",
        teacher: "",
        start_time: lastInBlock?.end_time || (block === "AM" ? "09:00" : "13:00"),
        end_time: "",
        block,
      };
      const next = [...p.slice(0, insertIdx), newRow, ...p.slice(insertIdx)];
      return next.map((r, i) => ({ ...r, period: i + 1 }));
    });
  };

  const removeAt = (g) => {
    setPeriods((p) => p.filter((_, idx) => idx !== g).map((r, i) => ({ ...r, period: i + 1 })));
  };

  const [pendingRemove, setPendingRemove] = useState(null); // {g, label} awaiting confirmation
  const confirmRemove = () => {
    if (pendingRemove) removeAt(pendingRemove.g);
    setPendingRemove(null);
  };

  const save = async () => {
    if (periods.some((p) => !p.subject || !p.teacher || !p.start_time || !p.end_time)) {
      toast.error("Please fill all period fields before saving.");
      return;
    }
    setSaving(true);
    try {
      if (defaultsMode) {
        await api.put("/timetable/default", { class_name: cn, section: sec, weekday, periods });
        toast.success(`${WEEKDAYS[weekday]} default timetable saved. It applies to every ${WEEKDAYS[weekday]} going forward.`);
      } else {
        await api.put("/timetable", { class_name: cn, section: sec, date, periods });
        toast.success("Timetable saved for this date only.");
      }
      setEditing(false);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const enterDefaults = () => { setDefaultsMode(true); setEditing(true); };
  const exitDefaults = () => { setDefaultsMode(false); setEditing(false); };

  const am = periods.filter((p) => p.block === "AM");
  const pm = periods.filter((p) => p.block === "PM");

  // A period is "running now" only in the daily view (not defaults/editing),
  // when the selected date is today and the clock is within its start–end.
  const toMin = (hm) => { const [h, m] = (hm || "").split(":").map((x) => parseInt(x, 10)); return Number.isFinite(h) ? h * 60 + (m || 0) : null; };
  const todayISO = new Date().toISOString().slice(0, 10);
  const isRunning = (p) => {
    if (editing || defaultsMode || date !== todayISO) return false;
    const s = toMin(p.start_time), e = toMin(p.end_time);
    return s != null && e != null && nowMin >= s && nowMin < e;
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Timetable</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>
            Class {cn} · Section {sec}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
            {defaultsMode
              ? "Editing the default weekly timetable — this applies to every selected weekday going forward."
              : "Morning and afternoon blocks are shown separately to match the attendance sessions."}
          </p>
          <div className="flex items-end gap-3 mt-4 flex-wrap">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Class</label>
              <Select value={cn} onValueChange={(v) => {
                const firstSec = (classMeta.assignments || []).filter((a) => a.class_name === v).map((a) => a.section).sort()[0];
                selectClass(v, firstSec || sec);
              }}>
                <SelectTrigger className="h-11 rounded-xl w-28" data-testid="timetable-class-select"><SelectValue /></SelectTrigger>
                <SelectContent>{classOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Section</label>
              <Select value={sec} onValueChange={(v) => selectClass(cn, v)}>
                <SelectTrigger className="h-11 rounded-xl w-24" data-testid="timetable-section-select"><SelectValue /></SelectTrigger>
                <SelectContent>{sectionOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {defaultsMode ? (
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Weekday</label>
                <Select value={String(weekday)} onValueChange={(v) => setWeekday(Number(v))}>
                  <SelectTrigger className="h-11 rounded-xl w-40" data-testid="timetable-weekday-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)} data-testid={`weekday-opt-${i}`}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Date</label>
                <DatePicker value={date} onChange={setDate} quickPicks className="h-11 rounded-xl" data-testid="timetable-date-input"/>
              </div>
            )}
            {defaultsMode ? (
              <>
                <Button className="h-11 rounded-xl bg-sky-500 hover:bg-sky-600" onClick={save} disabled={saving} data-testid="timetable-default-save-btn">
                  <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : `Save ${WEEKDAYS[weekday]} default`}
                </Button>
                <Button variant="outline" className="h-11 rounded-xl" onClick={exitDefaults} data-testid="timetable-defaults-exit-btn">Back to daily view</Button>
              </>
            ) : editing ? (
              <>
                <Button variant="outline" className="h-11 rounded-xl" onClick={() => { setEditing(false); load(); }} data-testid="timetable-cancel-btn">Cancel</Button>
                <Button className="h-11 rounded-xl bg-sky-500 hover:bg-sky-600" onClick={save} disabled={saving} data-testid="timetable-save-btn">
                  <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save this date"}
                </Button>
              </>
            ) : (
              <Button variant="outline" className="h-11 rounded-xl" onClick={() => setEditing(true)} data-testid="timetable-edit-btn">
                <Pencil className="h-4 w-4 mr-1" /> Edit timetable
              </Button>
            )}
          </div>
        </div>
        {!defaultsMode && !editing && !isReadOnly && (
          <div className="flex items-center gap-2 ml-auto">
            {!isReadOnly && (
              <Button variant="outline" className="h-11 rounded-xl" onClick={enterDefaults} data-testid="timetable-set-defaults-btn">
                <CalendarDays className="h-4 w-4 mr-1.5" /> Set weekday defaults
              </Button>
            )}
            <Button
              onClick={() => setOcOpen(true)}
              data-testid="schedule-online-btn"
              className="h-11 rounded-xl bg-emerald-500 hover:bg-emerald-600"
            >
              <Video className="h-4 w-4 mr-1.5" /> Schedule online class
            </Button>
          </div>
        )}
      </div>

      <OnlineClassPanel
        date={date}
        classSel={cn}
        secSel={sec}
        open={ocOpen}
        onClose={() => setOcOpen(false)}
        isReadOnly={isReadOnly}
      />

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">Loading timetable…</div>
      ) : (
        <div className="grid grid-cols-1 gap-6" data-testid="timetable-blocks">
          <BlockTable
            tone="sky"
            icon={Sunrise}
            title="Morning session"
            subtitle="Before-lunch periods (AM)"
            block="AM"
            rows={am}
            editing={editing}
            findGlobalIndex={findGlobalIndex}
            updateAt={updateAt}
            removeAt={(g, p) => setPendingRemove({ g, label: p ? `Period ${p.period}${p.subject ? " · " + p.subject : ""}` : "this period" })}
            onAdd={() => addPeriodToBlock("AM")}
            homeworkByPeriod={defaultsMode ? {} : homeworkByPeriod}
            onOpenHomework={(p) => setHomeworkFor(p)}
            canAssignHomework={!isReadOnly && !defaultsMode}
            subjects={options.subjects}
            teachers={options.teachers}
            isRunning={isRunning}
            testid="timetable-am"
          />
          <BlockTable
            tone="amber"
            icon={Sun}
            title="Afternoon session"
            subtitle="After-lunch periods (PM)"
            block="PM"
            rows={pm}
            editing={editing}
            findGlobalIndex={findGlobalIndex}
            updateAt={updateAt}
            removeAt={(g, p) => setPendingRemove({ g, label: p ? `Period ${p.period}${p.subject ? " · " + p.subject : ""}` : "this period" })}
            onAdd={() => addPeriodToBlock("PM")}
            homeworkByPeriod={defaultsMode ? {} : homeworkByPeriod}
            onOpenHomework={(p) => setHomeworkFor(p)}
            canAssignHomework={!isReadOnly && !defaultsMode}
            subjects={options.subjects}
            teachers={options.teachers}
            isRunning={isRunning}
            testid="timetable-pm"
          />
        </div>
      )}

      <HomeworkDialog
        open={!!homeworkFor}
        onClose={() => setHomeworkFor(null)}
        period={homeworkFor}
        classRoom={cn}
        section={sec}
        availableSections={sectionOptions}
        date={date}
        onSaved={loadHomework}
      />

      <AlertDialog open={!!pendingRemove} onOpenChange={(v) => !v && setPendingRemove(null)}>
        <AlertDialogContent data-testid="delete-period-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this period?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRemove?.label ? <><b>{pendingRemove.label}</b> will be removed. </> : null}
              This change applies when you save the timetable. You can cancel to keep it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-period-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600" data-testid="delete-period-confirm">Delete period</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function BlockTable({ tone, icon: Icon, title, subtitle, block, rows, editing, findGlobalIndex, updateAt, removeAt, onAdd, testid, homeworkByPeriod = {}, onOpenHomework, canAssignHomework, subjects = [], teachers = [], isRunning }) {
  const tones = {
    sky: { ring: "border-sky-200 dark:border-sky-900", bg: "bg-sky-50 dark:bg-sky-950/40", text: "text-sky-700 dark:text-sky-300", chip: "bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300" },
    amber: { ring: "border-amber-200 dark:border-amber-900", bg: "bg-amber-50 dark:bg-amber-950/40", text: "text-amber-700 dark:text-amber-300", chip: "bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300" },
  };
  const t = tones[tone];
  return (
    <div className={`bg-white dark:bg-slate-900 rounded-2xl border ${t.ring} overflow-hidden`} data-testid={testid}>
      <div className={`px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3 ${t.bg}`}>
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${t.chip}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>{title}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${t.chip}`} data-testid={`${testid}-count`}>{rows.length} period{rows.length === 1 ? "" : "s"}</span>
      </div>

      {rows.length === 0 && !editing ? (
        <div className="p-8 text-center text-slate-400 text-sm">No {block === "AM" ? "morning" : "afternoon"} periods scheduled.</div>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300 text-xs uppercase tracking-[0.15em]">
            <tr>
              <th className="px-3 py-3 text-left w-14">#</th>
              <th className="px-3 py-3 text-left">Subject</th>
              <th className="px-3 py-3 text-left">Teacher</th>
              <th className="px-3 py-3 text-left w-24">Start</th>
              <th className="px-3 py-3 text-left w-24">End</th>
              <th className="px-3 py-3 w-10"></th>
              {editing && <th className="px-3 py-3 w-10"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((p, i) => {
              const g = findGlobalIndex(block, i);
              const hwForRow = homeworkByPeriod[p.period] || [];
              const live = isRunning?.(p);
              return (
                <tr key={`${block}-${i}-${p.period}`} className={live ? "tt-live-row" : ""} data-testid={`${testid}-row-${i}`}>
                  <td className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">{p.period}</td>
                  <td className="px-3 py-3">
                    {editing ? (
                      <Select value={p.subject || ""} onValueChange={(v) => updateAt(g, "subject", v)}>
                        <SelectTrigger className="h-9 rounded-lg min-w-[120px]" data-testid={`${testid}-subject-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {[...new Set([...(p.subject ? [p.subject] : []), ...subjects])].map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (p.subject
                      ? <span className="inline-flex items-center gap-2">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${subjectColor(p.subject)}`} data-testid={`${testid}-subject-chip-${i}`}>{p.subject}</span>
                          {live && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5" data-testid={`${testid}-live-badge-${i}`}>
                              <span className="tt-live-dot h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
                            </span>
                          )}
                        </span>
                      : <span className="text-slate-300">—</span>)}
                  </td>
                  <td className="px-3 py-3">
                    {editing ? (
                      <TeacherCombobox
                        value={p.teacher || ""}
                        onChange={(v) => updateAt(g, "teacher", v)}
                        teachers={teachers}
                        subject={p.subject}
                        testid={`${testid}-teacher-${i}`}
                      />
                    ) : <span className="text-slate-600 dark:text-slate-300">{p.teacher}</span>}
                  </td>
                  <td className="px-3 py-3">
                    {editing ? (
                      <Input type="time" value={p.start_time} onChange={(e) => updateAt(g, "start_time", e.target.value)} className="h-9 rounded-lg" data-testid={`${testid}-start-${i}`}/>
                    ) : <span className="text-slate-600 dark:text-slate-300 font-mono">{to12h(p.start_time)}</span>}
                  </td>
                  <td className="px-3 py-3">
                    {editing ? (
                      <Input type="time" value={p.end_time} onChange={(e) => updateAt(g, "end_time", e.target.value)} className="h-9 rounded-lg" data-testid={`${testid}-end-${i}`}/>
                    ) : <span className="text-slate-600 dark:text-slate-300 font-mono">{to12h(p.end_time)}</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {canAssignHomework && (
                      <button
                        type="button"
                        onClick={() => onOpenHomework?.(p)}
                        title={hwForRow.length ? `${hwForRow.length} homework assigned — add another` : "Assign homework"}
                        data-testid={`${testid}-homework-${i}`}
                        className="relative p-2 rounded-lg hover:bg-sky-50 text-sky-600 hover:text-sky-700"
                      >
                        <BookOpen className="h-4 w-4" />
                        {hwForRow.length > 0 && (
                          <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-sky-500 text-white text-[10px] font-semibold flex items-center justify-center">
                            {hwForRow.length}
                          </span>
                        )}
                      </button>
                    )}
                  </td>
                  {editing && (
                    <td className="px-3 py-3">
                      <button onClick={() => removeAt(g, p)} className="text-slate-400 hover:text-rose-600" data-testid={`${testid}-remove-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {editing && (
        <div className="p-3 border-t border-slate-100">
          <Button variant="outline" className="rounded-full" onClick={onAdd} data-testid={`${testid}-add-btn`}>
            <Plus className="h-4 w-4 mr-1" /> Add {block === "AM" ? "morning" : "afternoon"} period
          </Button>
        </div>
      )}
    </div>
  );
}


function OnlineClassPanel({ date, classSel, secSel, open, onClose, isReadOnly }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [list, setList] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null); // Jitsi (legacy) { room, subject }
  const [dailyRoom, setDailyRoom] = useState(null); // { roomUrl, token, subject }
  const [joiningId, setJoiningId] = useState(null);

  const joinDailyHost = async (c) => {
    setJoiningId(c.id);
    try {
      const { data } = await api.post(`/online-classes/${c.id}/daily-token`);
      setDailyRoom({ roomUrl: data.room_url, token: data.token, subject: c.subject });
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not start the class");
    } finally { setJoiningId(null); }
  };
  const [form, setForm] = useState({
    subject: "",
    period: "",
    start_time: "09:00",
    end_time: "09:40",
    meeting_url: "",
    description: "",
    provider: "zoho",
    whole_school: false,
  });
  const [saving, setSaving] = useState(false);
  const [zohoHealth, setZohoHealth] = useState(null); // {configured, ok, error?}
  const [meta, setMeta] = useState({ assignments: [] });
  const [extra, setExtra] = useState([]); // ["class|section", ...] — additional included sections
  const [showExtra, setShowExtra] = useState(false);

  const otherSections = (meta.assignments || []).filter(
    (a) => !(String(a.class_name) === String(classSel) && String(a.section) === String(secSel))
  );
  const toggleExtra = (key) =>
    setExtra((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const load = async () => {
    try {
      const { data } = await api.get("/online-classes", { params: { class_name: classSel, section: secSel, date } });
      setList(data);
    } catch (e) { /* silent — list is optional */ }
  };

  // Probe Zoho status + load assignable sections when the dialog first opens.
  useEffect(() => {
    if (!open) return;
    setExtra([]); setShowExtra(false);
    api.get("/meta/classes").then(({ data }) => setMeta(data)).catch(() => {});
    (async () => {
      try {
        const { data } = await api.get("/online-classes/zoho/health");
        setZohoHealth(data);
        // If Zoho isn't configured, default the form to manual URL.
        if (data && !data.ok) setForm((f) => ({ ...f, provider: "manual" }));
      } catch (e) {
        setZohoHealth({ configured: false, ok: false, error: "Could not reach Zoho probe" });
        setForm((f) => ({ ...f, provider: "manual" }));
      }
    })();
  }, [open]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [classSel, secSel, date]);

  const submit = async () => {
    if (!form.subject || !form.start_time || !form.end_time) {
      toast.error("Subject, start and end times are required.");
      return;
    }
    if (form.provider === "manual" && !form.meeting_url) {
      toast.error("Meeting link is required when not using Zoho.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        class_name: classSel,
        section: secSel,
        date,
        subject: form.subject,
        period: form.period ? Number(form.period) : null,
        start_time: form.start_time,
        end_time: form.end_time,
        meeting_url: form.provider === "manual" ? form.meeting_url : "",
        description: form.description,
        provider: form.provider,
        timezone: "Asia/Kolkata",
        whole_school: isAdmin ? form.whole_school : false,
        sections: (isAdmin && form.whole_school) ? [] : extra.map((k) => {
          const [cn, sc] = k.split("|");
          return { class_name: cn, section: sc };
        }),
      };
      const { data } = await api.post("/online-classes", payload);
      if (form.provider === "zoho") {
        toast.success("Zoho meeting created. Join link is now visible to students.");
        // Surface host URL once so the teacher can copy if they want.
        if (data?.host_url) {
          // eslint-disable-next-line no-console
          console.info("Zoho host link:", data.host_url);
        }
      } else {
        toast.success("Online class scheduled.");
      }
      setForm((f) => ({ ...f, subject: "", meeting_url: "", description: "" }));
      onClose();
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not schedule");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this online class?")) return;
    try {
      await api.delete(`/online-classes/${id}`);
      toast.success("Deleted.");
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <>
      {list.length > 0 && (
        <div className="mb-6 rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-white dark:bg-slate-800 p-5" data-testid="online-class-panel">
          <div className="flex items-center gap-2 mb-3">
            <Video className="h-4 w-4 text-emerald-600" />
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>
              Online classes on {date} ({list.length})
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {list.map((c) => (
              <div key={c.id} className="rounded-xl bg-emerald-50/50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900 p-3 text-sm" data-testid={`oc-row-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">{c.subject}</div>
                      {c.provider === "zoho" && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider bg-emerald-500 text-white px-1.5 py-0.5 rounded" data-testid={`oc-provider-badge-${c.id}`}>Zoho</span>
                      )}
                      {(c.provider === "jitsi" || c.provider === "daily") && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider bg-indigo-500 text-white px-1.5 py-0.5 rounded" data-testid={`oc-provider-badge-${c.id}`}>In-app</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{to12h(c.start_time)}–{to12h(c.end_time)}{c.period ? ` · Period ${c.period}` : ""}</div>
                    {c.provider === "daily" ? (
                      <button type="button" onClick={() => joinDailyHost(c)} disabled={joiningId === c.id} className="inline-flex items-center gap-1 text-xs text-indigo-700 dark:text-indigo-300 hover:underline mt-1" data-testid={`oc-join-inapp-${c.id}`}>
                        <Video className="h-3 w-3" /> {joiningId === c.id ? "Starting…" : "Join in-app (as host)"}
                      </button>
                    ) : c.provider === "jitsi" ? (
                      <button type="button" onClick={() => setActiveRoom({ room: c.room_name, subject: c.subject })} className="inline-flex items-center gap-1 text-xs text-indigo-700 dark:text-indigo-300 hover:underline mt-1" data-testid={`oc-join-inapp-${c.id}`}>
                        <Video className="h-3 w-3" /> Join in-app (as host)
                      </button>
                    ) : (
                      <a href={c.meeting_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300 hover:underline mt-1 truncate max-w-full">
                        <ExternalLink className="h-3 w-3" /> Open join link
                      </a>
                    )}
                    {c.host_url && c.host_url !== c.meeting_url && (
                      <a href={c.host_url} target="_blank" rel="noreferrer" className="block text-xs text-sky-600 dark:text-sky-300 hover:underline mt-0.5 truncate max-w-full" data-testid={`oc-host-link-${c.id}`}>
                        <ExternalLink className="h-3 w-3 inline mr-1" /> Start as host
                      </a>
                    )}
                  </div>
                  {!isReadOnly && (
                    <button type="button" onClick={() => remove(c.id)} className="text-slate-400 hover:text-rose-600 shrink-0" data-testid={`oc-delete-${c.id}`} aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="sm:max-w-lg" data-testid="schedule-online-dialog">
          <DialogHeader>
            <DialogTitle style={{fontFamily:"Outfit"}}>Schedule online class</DialogTitle>
            <DialogDescription>Set the meeting details to schedule an online class for this period.</DialogDescription>
          </DialogHeader>

          {isAdmin && (
            <label
              className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                form.whole_school
                  ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30"
                  : "border-slate-200 dark:border-slate-700 hover:border-emerald-300"
              }`}
              data-testid="oc-whole-school-toggle"
            >
              <input
                type="checkbox"
                checked={form.whole_school}
                onChange={(e) => setForm({ ...form, whole_school: e.target.checked })}
                className="mt-0.5 h-4 w-4 accent-emerald-500"
                data-testid="oc-whole-school-checkbox"
              />
              <span className="text-sm">
                <span className="font-semibold text-slate-900 dark:text-slate-100">🏫 Entire school (all sections)</span>
                <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Broadcast to every teacher &amp; student. A live "Join now" banner appears for everyone when it starts.
                </span>
              </span>
            </label>
          )}

          {/* Provider toggle */}
          <div className="grid grid-cols-3 gap-2 p-1 rounded-xl bg-slate-100 dark:bg-slate-700/50 mb-2" data-testid="oc-provider-toggle">
            <button
              type="button"
              onClick={() => setForm({...form, provider: "daily"})}
              data-testid="oc-provider-jitsi"
              className={`text-xs font-semibold rounded-lg py-2 px-3 transition-colors ${
                form.provider === "daily"
                  ? "bg-white dark:bg-slate-900 text-indigo-600 shadow-sm"
                  : "text-slate-600 dark:text-slate-300 hover:text-slate-900"
              }`}
            >
              In-app video
            </button>
            <button
              type="button"
              onClick={() => setForm({...form, provider: "zoho"})}
              disabled={zohoHealth && !zohoHealth.ok}
              data-testid="oc-provider-zoho"
              className={`text-xs font-semibold rounded-lg py-2 px-3 transition-colors ${
                form.provider === "zoho"
                  ? "bg-white dark:bg-slate-900 text-emerald-600 shadow-sm"
                  : "text-slate-600 dark:text-slate-300 hover:text-slate-900 disabled:opacity-50"
              }`}
            >
              Zoho Meeting
            </button>
            <button
              type="button"
              onClick={() => setForm({...form, provider: "manual"})}
              data-testid="oc-provider-manual"
              className={`text-xs font-semibold rounded-lg py-2 px-3 transition-colors ${
                form.provider === "manual"
                  ? "bg-white dark:bg-slate-900 text-sky-600 shadow-sm"
                  : "text-slate-600 dark:text-slate-300 hover:text-slate-900"
              }`}
            >
              Paste link
            </button>
          </div>
          {form.provider === "daily" && (
            <div className="text-xs text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900 rounded-lg p-2.5" data-testid="oc-jitsi-note">
              A private in-app video room is created automatically. You (the teacher) join as the HOST with full mute / lock / remove controls. Students join in one click — no new tab, no login.
            </div>
          )}
          {form.provider === "zoho" && zohoHealth && !zohoHealth.ok && (
            <div className="text-xs text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 rounded-lg p-2.5" data-testid="oc-zoho-warn">
              Zoho is not configured yet. {zohoHealth.configured ? "Token refresh failed." : "Ask admin to add ZOHO_* env vars."}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <Field label="Subject" full>
              <Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} placeholder="e.g. Mathematics" data-testid="oc-subject-input" />
            </Field>
            <Field label="Start time">
              <Input type="time" value={form.start_time} onChange={(e) => setForm({...form, start_time: e.target.value})} data-testid="oc-start-input" />
            </Field>
            <Field label="End time">
              <Input type="time" value={form.end_time} onChange={(e) => setForm({...form, end_time: e.target.value})} data-testid="oc-end-input" />
            </Field>
            {form.provider === "manual" && (
              <Field label="Meeting URL" full>
                <Input value={form.meeting_url} onChange={(e) => setForm({...form, meeting_url: e.target.value})} placeholder="https://meet.google.com/…" data-testid="oc-url-input" />
              </Field>
            )}
            <Field label="Notes (optional)" full>
              <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Anything students should know" data-testid="oc-notes-input" />
            </Field>
          </div>
          {otherSections.length > 0 && !(isAdmin && form.whole_school) && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3" data-testid="oc-extra-sections">
              <button type="button" onClick={() => setShowExtra((v) => !v)} className="w-full flex items-center justify-between text-sm font-semibold text-slate-800 dark:text-slate-100" data-testid="oc-extra-toggle">
                <span>Include students from other classes {extra.length > 0 ? `(${extra.length} added)` : "(optional)"}</span>
                {showExtra ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {showExtra && (
                <>
                  <p className="text-xs text-slate-500 mt-1 mb-2">The primary class <b>{classSel}-{secSel}</b> is always included. Tick any additional sections whose students should also be able to join this meeting.</p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-40 overflow-y-auto pr-1">
                    {otherSections.map((a) => {
                      const key = `${a.class_name}|${a.section}`;
                      const on = extra.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleExtra(key)}
                          data-testid={`oc-extra-${a.class_name}-${a.section}`}
                          className={`text-xs font-medium rounded-lg py-1.5 px-2 border transition-colors ${
                            on
                              ? "bg-indigo-500 text-white border-indigo-500"
                              : "bg-transparent text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-indigo-400"
                          }`}
                        >
                          {a.class_name}-{a.section}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="text-xs text-slate-500">
            {isAdmin && form.whole_school
              ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Entire school (all sections)</span>
              : <>Class {classSel}-{secSel}{extra.length > 0 ? ` + ${extra.map((k) => k.replace("|", "-")).join(", ")}` : ""}</>} · {date}
            {form.provider === "zoho" && <span className="text-emerald-600 dark:text-emerald-400 ml-1">· Join link will be generated by Zoho</span>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600" data-testid="oc-submit-btn">
              {saving ? "Saving…" : (form.provider === "zoho" ? "Create with Zoho" : "Schedule")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {activeRoom && (
        <JitsiRoomOverlay
          room={activeRoom.room}
          subject={activeRoom.subject}
          displayName={user?.name || "Teacher"}
          email={user?.email}
          isHost={true}
          onClose={() => setActiveRoom(null)}
        />
      )}
      {dailyRoom && (
        <DailyRoomOverlay
          roomUrl={dailyRoom.roomUrl}
          token={dailyRoom.token}
          subject={dailyRoom.subject}
          isHost={true}
          onClose={() => setDailyRoom(null)}
        />
      )}
    </>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
      <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
